import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { splitHint } from '../lib/routeSplitText';
import { LoggerIcon } from './LoggerIcon';
import { Route, CalendarClock, Play, Volume2, ScanSearch } from 'lucide-react';
import { flyTo, setVis } from '../lib/vis';
import { startRound, openPlanTab } from './lpShared';
import { planRound, lastFix, warmFix, roundOpts } from '../lib/route';
import { LegList, saveRound, clearRound, loadRound } from './LegList';
import { RoundOpts } from './RoundOpts';
import { rt, fill } from '../lib/roundText';
import { matchAll, lm } from '../lib/leakMatch';
import { loggerState, activeLoggers, noiseSpots } from '../hooks/useLoggers';

// 1-click Google Maps routes: due lift & shift loggers / all loggers out
export const loggerSets = (project) => {
  const all = activeLoggers(project);
  return { due: all.filter((l) => ['red', 'yellow'].includes(loggerState(l))), all };
};

export default function LoggerRoutes({ t, project, start, compact, updateProject }) {
  const { due, all } = loggerSets(project);
  const ctx = compact ? 'lgc' : 'lg';
  const [order, setOrder] = useState(() => loadRound(ctx)?.ord || []);
  const [km, setKm] = useState(() => loadRound(ctx)?.km || 0);
  const saved = loadRound(ctx);
  useEffect(() => { warmFix(); }, []);
  const lifts = all.filter((l) => l.mode !== 'perm' && l.dueAt).sort((a, b) => a.dueAt - b.dueAt);
  const plan = project.loggerPlan || [], planOpen = plan.filter((p) => !p.doneAt);
  const showPlan = compact && planOpen.length > 0 && updateProject;
  const noise = compact ? noiseSpots(project) : [];
  const conf = noise.length ? matchAll(project).noise.filter((z) => z.m).length : 0;
  if (!all.length && !showPlan && !noise.length) return null;
  // route always starts at the current position (GPS); Google Maps "my location" if no fix
  // synchronous inside the tap: a pre-opened blank tab stays white on Android when Maps takes over the link
  const go = (list, keep) => {
    const here = (start?.gps === false ? null : start) || lastFix();
    const stops = list.map((l) => ({ ...l, title: l.name })), { ord, legs: ls, km: k } = planRound(here, stops, keep);
    const slim = ord.map((l) => ({ id: l.id, name: l.name, dueAt: l.dueAt || null }));
    setOrder(slim); setKm(k);
    if (ls.length === 1) { window.open(ls[0].url, '_blank'); clearRound(ctx); return; }
    saveRound(ctx, ls, { ord: slim, km: k, n: list.length }); toast.info(splitHint(list.length, ls.length), { duration: 10000 });
  };
  return (
    <div className={`lg-routes ${compact ? 'lg-routes-card panel' : ''}`} data-testid={compact ? 'logger-card' : 'logger-routes'}>
      {compact && <b className="lg-card-title"><LoggerIcon size={16} /> {t.lgTitle}: <span data-testid="logger-count-out">{all.length}</span> {t.lgOut} · <span data-testid="logger-count-due">{due.length}</span> {t.lgDueShort}</b>}
      {showPlan && (
        <div className="lg-card-plan" data-testid="card-plan-round">
          <b data-testid="card-plan-count">🗺 {t.lrTitle}: {planOpen.length} {t.lrPlanned}</b>
          <button className="btn btn-green btn-sm" data-testid="card-plan-start-btn" onClick={() => { startRound(plan, (list) => updateProject((p) => ({ ...p, loggerPlan: list })), plan.some((p) => p.doneAt)); openPlanTab('deploy'); }}><Play size={14} /> {plan.some((p) => p.doneAt) ? t.lrGoOn : t.lrStart}</button>
        </div>
      )}
      {noise.length > 0 && (
        <div className="lg-card-plan" data-testid="card-noise">
          <b className="ns-count" data-testid="card-noise-count"><Volume2 size={14} /> {noise.length} {noise.length === 1 ? t.nsOne : t.nsTitle}</b>{conf > 0 && <small className="ns-conf" data-testid="card-noise-confirmed">✓ {conf} {lm().ok}</small>}
          <button className="btn btn-outline btn-sm" data-testid="card-noise-show-btn" onClick={() => { setVis({ noise: true }); flyTo(noise.map((s) => [s.lat, s.lon])); }}><ScanSearch size={14} /> {t.nsShow}</button>
          <button className="btn btn-red btn-sm" data-testid="card-noise-route-btn" onClick={() => { setVis({ noise: true }); go(noise.map((s) => ({ ...s, name: s.id.endsWith('-c') ? s.name : `${s.name} (${t.nsOld})` }))); }}><Route size={14} /> {t.nsRoute}</button>
        </div>
      )}
      {all.length > 0 && <>
      <button className="btn btn-red btn-sm" data-testid={`${compact ? 'card' : 'route'}-logger-due-btn`} disabled={!due.length} onClick={() => go(due)}><Route size={14} /> {t.lgRouteDue} ({due.length})</button>
      {lifts.length > 0 && <button className="btn btn-outline btn-sm" data-testid={`${compact ? 'card' : 'route'}-logger-bydue-btn`} onClick={() => go(lifts, true)}><CalendarClock size={14} /> {t.lpRouteDue} ({lifts.length})</button>}
      <button className="btn btn-primary btn-sm" data-testid={`${compact ? 'card' : 'route'}-logger-all-btn`} onClick={() => go(all)}><LoggerIcon size={14} /> {t.lgRouteAll} ({all.length})</button>
      </>}
      {all.length > 1 && !compact && <RoundOpts pre="logger" />}
      {saved?.legs.length > 1 && <small className="lg-split-hint" data-testid="logger-legs-hint">{splitHint(saved.n, saved.legs.length)}</small>}
      <LegList ctx={ctx} t={t} pre={compact ? 'lgc' : 'logger'} />
      {order.length > 0 && km > 0 && <small className="ro-km" data-testid="logger-round-km">{fill(rt().km, { km: km.toFixed(1) })}</small>}
      {order.length > 0 && <ol className="lg-order" data-testid="logger-route-order"><li data-testid="logger-route-start">{roundOpts().start === 'first' ? `① ${rt().start}: ${rt().first}` : `📍 ${t.lgFromHere}`}</li>{order.map((l) => <li key={l.id}>{l.name}{l.dueAt ? ` · ${new Date(l.dueAt).toLocaleString(t.locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}</li>)}</ol>}
    </div>
  );
}
